import { useEffect } from 'react';

/**
 * Rings while an incoming video call is waiting to be answered
 * (ST-20260923-0005). Generated with Web Audio, so there is no sound file to
 * load: a UK-style double ring (400 + 450 Hz, 0.4 s on, 0.2 s off, 0.4 s on,
 * 2 s off), repeated until the call is answered, declined or withdrawn, and
 * never for more than MAX_RING_MS.
 *
 * Browsers only allow sound after the person has interacted with the page. If
 * the browser refuses, the call box still appears exactly as before; nothing
 * here throws.
 */

const CYCLE_MS = 3000;
const MAX_RING_MS = 60 * 1000;
const TONES_HZ = [400, 450];
const BURSTS = [[0, 0.4], [0.6, 1.0]]; // seconds from the start of each cycle
const VOLUME = 0.15;

function scheduleCycle(ctx, destination) {
  const start = ctx.currentTime + 0.05;
  for (const [on, off] of BURSTS) {
    for (const hz of TONES_HZ) {
      const osc = ctx.createOscillator();
      osc.frequency.value = hz;
      osc.connect(destination);
      osc.start(start + on);
      osc.stop(start + off);
    }
  }
}

export default function useRingtone(active) {
  useEffect(() => {
    if (!active) return undefined;
    const AudioCtx = typeof window !== 'undefined' && (window.AudioContext || window.webkitAudioContext);
    if (!AudioCtx) return undefined;

    let ctx;
    let interval;
    let stopTimer;
    try {
      ctx = new AudioCtx();
      const gain = ctx.createGain();
      gain.gain.value = VOLUME / TONES_HZ.length;
      gain.connect(ctx.destination);
      const ring = () => {
        try { scheduleCycle(ctx, gain); } catch (_) { /* a failed ring is silent, never an error */ }
      };
      if (ctx.state === 'suspended' && ctx.resume) ctx.resume().catch(() => {});
      ring();
      interval = setInterval(ring, CYCLE_MS);
      stopTimer = setTimeout(() => clearInterval(interval), MAX_RING_MS);
    } catch (_) {
      return undefined;
    }

    return () => {
      clearInterval(interval);
      clearTimeout(stopTimer);
      try { ctx.close(); } catch (_) { /* already closed */ }
    };
  }, [active]);
}

export { CYCLE_MS, MAX_RING_MS };
